import moment from "moment-timezone";

const CRM_STATUSES = ["new", "contacted", "needs_help", "waiting", "active", "churned", "closed"];
const TIME_ZONE = "Europe/Prague";
export const CRM_FORM_CONTROL_CLASS = "w-full rounded-xl border border-[#d8d8e4] bg-white text-sm text-slate-700 shadow-none outline-none ring-0 ring-offset-0 transition-colors hover:border-violet-300 focus:border-violet-400 focus:shadow-none focus:ring-2 focus:ring-violet-100 focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-violet-400 dark:focus:border-violet-400 dark:focus:ring-violet-500/20";
export const CRM_DELETION_BADGE_CLASS = "!border !border-rose-500 !bg-transparent !text-datixo-negative dark:!border-rose-400 dark:!bg-transparent";
export const CRM_SELECT_STYLES = {
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? "#a78bfa" : "#d8d8e4",
        borderWidth: 1,
        borderRadius: 12,
        boxShadow: state.isFocused ? "0 0 0 3px rgba(167, 139, 250, 0.14)" : "none",
        "&:hover": {borderColor: state.isFocused ? "#a78bfa" : "#c4b5fd"},
    }),
    menuPortal: (base) => ({...base, zIndex: 9999}),
};

function parseDate(value) {
    if (!value) return null;
    const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
    const parsed = hasOffset ? moment.parseZone(value).tz(TIME_ZONE) : moment.tz(value, TIME_ZONE);
    return parsed.isValid() ? parsed : null;
}

export function crmStatusLabel(status, t) {
    const labels = {
        new: t("admin.project_crm.status_new", {defaultValue: "K oslovení"}),
        contacted: t("admin.project_crm.status_contacted", {defaultValue: "Kontaktován"}),
        needs_help: t("admin.project_crm.status_needs_help", {defaultValue: "Potřebuje pomoc"}),
        waiting: t("admin.project_crm.status_waiting", {defaultValue: "Čeká na odpověď"}),
        active: t("admin.project_crm.status_active", {defaultValue: "V péči"}),
        churned: t("admin.project_crm.status_churned", {defaultValue: "Odešel"}),
        closed: t("admin.project_crm.status_closed", {defaultValue: "Uzavřený"}),
    };
    return labels[status] ?? status ?? "—";
}

export function crmStatusBadgeColor(status) {
    const colors = {
        new: "slate",
        contacted: "blue",
        needs_help: "yellow",
        waiting: "violet",
        active: "emerald",
        churned: "rose",
        closed: "green",
    };
    return colors[status] ?? "slate";
}

export function crmStatusOptions(t) {
    return CRM_STATUSES.map((value) => ({value, label: crmStatusLabel(value, t)}));
}

export function crmSubscriptionTypeOptions(t) {
    return [
        {value: "all", label: t("admin.project_crm.subscription_filter_all", {defaultValue: "Všechna předplatná"})},
        {value: "trial", label: t("admin.project_crm.subscription_filter_trial", {defaultValue: "Zkušební zdarma"})},
        {value: "paid", label: t("admin.project_crm.subscription_filter_paid", {defaultValue: "Aktivní placené"})},
        {value: "free", label: t("admin.project_crm.subscription_filter_free", {defaultValue: "Bezplatný balíček"})},
        {value: "none", label: t("admin.project_crm.subscription_filter_none", {defaultValue: "Bez aktivního balíčku"})},
        {value: "unknown", label: t("admin.project_crm.subscription_filter_unknown", {defaultValue: "Neznámé předplatné"})},
    ];
}

export function subscriptionStatusLabel(status, t) {
    const labels = {
        active: t("admin.project_crm.subscription_active", {defaultValue: "Aktivní"}),
        pending: t("admin.project_crm.subscription_pending", {defaultValue: "Čekající"}),
        past_due: t("admin.project_crm.subscription_past_due", {defaultValue: "Po splatnosti"}),
        canceled: t("admin.project_crm.subscription_canceled", {defaultValue: "Zrušené"}),
        cancelled: t("admin.project_crm.subscription_canceled", {defaultValue: "Zrušené"}),
        expired: t("admin.project_crm.subscription_expired", {defaultValue: "Vypršené"}),
    };
    return labels[status] ?? t("admin.project_crm.subscription_status_unknown", {defaultValue: "Stav předplatného neznámý"});
}

export function isCrmSubscriptionKnownAbsent(project) {
    return crmSubscriptionState(project) === "none";
}

export function crmSubscriptionState(project) {
    if (project?.subscription) return "present";
    if (project?.subscriptionKnowledge === "known") return "none";
    if (project?.subscriptionKnowledge === "unknown") return "unknown";
    // Older payloads have no explicit knowledge field; only use evidence available there.
    if (!project?.isDeleted || project?.snapshot?.schemaVersion === 1) return "none";
    return "unknown";
}

export function crmBillingState(project, snapshot = null) {
    const billing = project?.billing ?? snapshot?.billing ?? null;
    const hasValues = Object.values(billing ?? {}).some((value) => value !== null && value !== undefined && String(value).trim() !== "");
    if (hasValues) return "present";
    if (project?.isDeleted && snapshot?.schemaVersion !== 1) return "unknown";
    return "none";
}

export function subscriptionBillingCycleLabel(cycle, t) {
    const labels = {
        monthly: t("admin.project_crm.billing_cycle_monthly", {defaultValue: "Měsíčně"}),
        annual: t("admin.project_crm.billing_cycle_annual", {defaultValue: "Ročně"}),
    };
    return labels[cycle] ?? t("admin.project_crm.billing_cycle_unknown", {defaultValue: "Fakturační období neznámé"});
}

export function formatSubscriptionNetPrice(subscription, locale, t) {
    const price = subscription?.packageNetPrice;
    if (price === null || price === undefined || !Number.isFinite(Number(price))) {
        return t("admin.project_crm.subscription_price_unknown", {defaultValue: "Cena neznámá"});
    }

    const currency = subscription.packageCurrency || "CZK";
    let formattedPrice;
    try {
        formattedPrice = new Intl.NumberFormat(locale, {style: "currency", currency}).format(Number(price));
    } catch {
        return t("admin.project_crm.subscription_price_unknown", {defaultValue: "Cena neznámá"});
    }

    if (subscription.billingCycle === "monthly") {
        return t("admin.project_crm.subscription_price_monthly", {price: formattedPrice, defaultValue: "{{price}} / měsíc"});
    }
    if (subscription.billingCycle === "annual") {
        return t("admin.project_crm.subscription_price_annual", {price: formattedPrice, defaultValue: "{{price}} / rok"});
    }
    return t("admin.project_crm.subscription_price_period_unknown", {price: formattedPrice, defaultValue: "{{price}} · období neznámé"});
}

export function buildProjectCrmListQuery({page, pageSize, search, category = "all", crmStatus, integrationType, subscriptionType = "all"}) {
    const query = {page, pageSize, category, integrationScope: "ever", subscriptionType};
    const normalizedSearch = search?.trim();
    if (normalizedSearch) query.search = normalizedSearch;
    if (crmStatus) query.crmStatus = crmStatus;
    if (integrationType) query.integrationType = integrationType;
    return query;
}

export async function isCrmOpportunityStorageUnavailable(error) {
    if (error?.response?.status !== 503 || typeof error.response.clone !== "function") return false;
    try {
        const body = await error.response.clone().json();
        return typeof body?.message === "string" && body.message.includes("CRM opportunity storage is not installed");
    } catch {
        return false;
    }
}

export const CRM_OPPORTUNITY_NOTES_PAGE_SIZE = 5;
export const CRM_OPPORTUNITY_NOTES_API_PAGE_SIZE = 10;

export function crmOpportunityNotesPage(page) {
    const safePage = Number.isInteger(page) && page > 0 ? page : 1;
    const offset = (safePage - 1) * CRM_OPPORTUNITY_NOTES_PAGE_SIZE;
    return {
        apiPage: Math.floor(offset / CRM_OPPORTUNITY_NOTES_API_PAGE_SIZE) + 1,
        offset: offset % CRM_OPPORTUNITY_NOTES_API_PAGE_SIZE,
    };
}

export function changeCrmPageSize(setPage, setPageSize, pageSize) {
    setPage(1);
    setPageSize(pageSize);
}

export function updateProjectCrmListRow(data, recordId, changes) {
    if (!data?.items) return data;
    const target = data.items.find((item) => item.recordType === "opportunity" ? item.crmId === recordId : item.projectId === recordId);
    if (!target) return data;
    const resolvedChanges = typeof changes === "function" ? changes(target) : changes;
    return {
        ...data,
        items: data.items.map((item) => {
            if (item.recordType === "opportunity" ? item.crmId !== recordId : item.projectId !== recordId) return item;
            const updated = {...item, ...resolvedChanges};
            if (Object.hasOwn(resolvedChanges, "primaryContactUserId")) {
                const contact = (resolvedChanges.contacts ?? item.contacts ?? []).find((candidate) => candidate.userId === resolvedChanges.primaryContactUserId && !candidate.isAdministrator);
                updated.primaryContact = contact ? {
                    ...contact,
                    userId: contact.userId ?? null,
                    contactOrigin: resolvedChanges.primaryContactOrigin ?? contact.contactOrigin ?? "unknown",
                } : null;
            }
            return updated;
        }),
    };
}

export function canActivateCrmProject(project) {
    return project?.recordType !== "opportunity" && Boolean(project?.projectId) && project?.isDeleted !== true && project?.lifecycle !== "deleted";
}

export function toPragueDateTimeInput(value) {
    if (!value) return "";
    const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
    const parsed = hasOffset ? moment.parseZone(value).tz(TIME_ZONE) : moment.tz(value, TIME_ZONE);
    return parsed.isValid() ? parsed.format("YYYY-MM-DDTHH:mm") : "";
}

export function fromPragueDateTimeInput(value) {
    if (!value) return null;
    const parsed = moment.tz(value, "YYYY-MM-DDTHH:mm", true, TIME_ZONE);
    return parsed.isValid() ? parsed.toDate() : null;
}

// Each form updates its own CRM contract so contact edits cannot clear a saved follow-up.
export function buildCrmOpportunityUpdate(draft, scope) {
    if (scope === "workflow") return {crmStatus:draft.crmStatus,nextContactAt:fromPragueDateTimeInput(draft.nextContactAt)};
    return {name:draft.name.trim(),contactName:draft.contactName.trim() || null,email:draft.email.trim() || null,phone:draft.phone.trim() || null};
}

// A successful save reconciles only its form; the other tab can contain unsaved edits.
export function reconcileCrmOpportunityDraft(draft, updated, scope) {
    if (scope === "workflow") return {...draft, crmStatus: updated.crmStatus ?? draft.crmStatus, nextContactAt: toPragueDateTimeInput(updated.nextContactAt)};
    return {...draft, name: updated.name ?? draft.name, contactName: updated.primaryContact?.name ?? "", email: updated.primaryContact?.email ?? "", phone: updated.primaryContact?.phone ?? ""};
}

export function integrationPresentationState(integration) {
    if (integration?.removedAt) return "removed";
    if (["shoptet_addon", "upgates_addon"].includes(integration?.type)) {
        if (integration.status === "inactive") return "deactivated";
        if (integration.status === "paused") return "paused";
        if (integration.status !== "active") return "unknown";
        if (integration.paused === true) return "paused";
        if (integration.authorizationSuccessful === false) return "inactive";
        return integration.authorizationSuccessful === true && integration.paused === false ? "current" : "unknown";
    }
    if (integration?.paused === true) return "paused";
    if (integration?.authorizationSuccessful === false) return "inactive";
    if (integration?.authorizationSuccessful === true) return "current";
    return "unknown";
}

export function integrationPresentationColor(state) {
    return ({current: "emerald", paused: "yellow", inactive: "rose", deactivated: "rose", removed: "slate", unknown: "slate"})[state] ?? "slate";
}

export function integrationPresentationLabel(state, t) {
    const labels = {
        current: t("admin.project_crm.integration_connected", {defaultValue: "Připojeno"}),
        "not-selected": t("admin.project_crm.integration_no_selected_account", {defaultValue: "Bez vybraného účtu"}),
        paused: t("admin.project_crm.integration_paused", {defaultValue: "Pozastaveno"}),
        inactive: t("admin.project_crm.integration_auth_failed", {defaultValue: "Autorizace neúspěšná"}),
        deactivated: t("admin.project_crm.integration_deactivated", {defaultValue: "Doplněk deaktivován"}),
        removed: t("admin.project_crm.integration_removed", {defaultValue: "Odpojeno"}),
        unknown: t("admin.project_crm.integration_state_unknown", {defaultValue: "Stav neznámý"}),
    };
    return labels[state] ?? labels.unknown;
}

export function sortCrmIntegrations(integrations = []) {
    const typeRanks = {
        bs_shop: 0,
        shoptet: 0,
        upgates: 0,
        shoptet_addon: 1,
        upgates_addon: 1,
        mysql_leads: 2,
    };
    const otherStateRanks = {current: 0, paused: 1, inactive: 2, deactivated: 2, unknown: 3};
    return integrations.map((integration, index) => ({integration, index}))
        .sort((left, right) => {
            const leftState = integrationPresentationState(left.integration);
            const rightState = integrationPresentationState(right.integration);
            if (leftState === "removed" || rightState === "removed") {
                return Number(leftState === "removed") - Number(rightState === "removed") || left.index - right.index;
            }

            const leftTypeRank = typeRanks[left.integration?.type];
            const rightTypeRank = typeRanks[right.integration?.type];
            if (leftTypeRank !== undefined || rightTypeRank !== undefined) {
                return (leftTypeRank ?? 3) - (rightTypeRank ?? 3) || left.index - right.index;
            }

            return otherStateRanks[leftState] - otherStateRanks[rightState] || left.index - right.index;
        })
        .map(({integration}) => integration);
}

export function resourcePresentationState(resource) {
    if (resource?.removedAt) return "removed";
    if (resource?.enabled === true) return "enabled";
    if (resource?.enabled === false) return "disabled";
    return "unknown";
}

const SELECTABLE_ACCOUNT_RESOURCE_TYPES = new Set([
    "project_ad_system_ad_account", "meta_ads_ad_account", "google_ads_customer",
    "external_integration_meta_ads_ad_account", "external_integration_google_ads_customer_client",
    "external_integration_google_analytics_property", "external_integration_google_search_console_site",
    "external_integration_google_merchant_account", "external_integration_microsoft_customer",
    "external_integration_google_bigquery_recipe",
]);

function resourceType(resource) {
    return String(resource?.type ?? "").toLowerCase();
}

export function isTechnicalIntegrationResource(resource) {
    return resourceType(resource).startsWith("external_integrations_host_name_");
}

export function technicalIntegrationResourceLabel(resource, t) {
    const type = resourceType(resource);
    if (type.endsWith("_and_csv_link")) return t("admin.project_crm.technical_csv_feed", {defaultValue: "Propojení přes CSV"});
    if (type.endsWith("_and_xml_link")) return t("admin.project_crm.technical_xml_feed", {defaultValue: "Propojení přes XML"});
    if (type.includes("api_id_api_key")) return t("admin.project_crm.technical_api_connection", {defaultValue: "API propojení"});
    if (type.includes("tld_and_currency")) return t("admin.project_crm.technical_shop_settings", {defaultValue: "Nastavení obchodu"});
    return t("admin.project_crm.technical_settings_item", {defaultValue: "Technické nastavení"});
}

export function isSelectableIntegrationAccount(resource) {
    return SELECTABLE_ACCOUNT_RESOURCE_TYPES.has(resourceType(resource));
}

/** De-duplicates only exact mirrors inside this one connection; unnamed identities stay separate. */
export function uniqueIntegrationResources(resources = []) {
    const seen = new Set();
    return resources.filter((resource) => {
        if (isTechnicalIntegrationResource(resource)) return true;
        const remoteId = resource?.remoteId ?? resource?.remote_id ?? resource?.account_id ?? resource?.customer_client_id ?? resource?.property_id ?? resource?.site_url ?? resource?.dataset_id;
        if (remoteId === null || remoteId === undefined || remoteId === "" || !resource?.name) return true;
        const key = JSON.stringify([String(resource.name).trim().toLocaleLowerCase(), String(remoteId), resourcePresentationState(resource)]);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

export function integrationDisplayState(integration) {
    const state = integrationPresentationState(integration);
    if (state !== "current") return state;
    const accountResources = (integration?.resources ?? []).filter(isSelectableIntegrationAccount);
    if (accountResources.length > 0 && accountResources.every((resource) => ["disabled", "removed"].includes(resourcePresentationState(resource)))) return "not-selected";
    return state;
}

export function integrationSecondaryFacts(integration, t) {
    const facts = [];
    if (integrationPresentationState(integration) === "paused" && integration?.authorizationSuccessful === false) {
        facts.push(t("admin.project_crm.integration_auth_failed", {defaultValue: "Autorizace neúspěšná"}));
    } else if (integration?.paused === true && integration?.authorizationSuccessful === true) {
        facts.push(t("admin.project_crm.integration_authorized", {defaultValue: "Autorizace úspěšná"}));
    }
    return facts;
}

const INTEGRATION_LIFECYCLE_ENTITY_TYPES = new Set([
    "external_integration_meta_ads", "external_integration_tiktok", "external_integration_bs_shop",
    "external_integration_criteo", "external_integration_sklik", "external_integration_google_ads",
    "external_integration_upgates", "external_integration_google_analytics", "external_integration_pinterest",
    "external_integration_mysql_leads", "external_integration_zbozi", "external_integration_upgates_addon",
    "external_integration_heureka", "external_integration_glami", "external_integration_shoptet_addon",
    "external_integration_shoptet", "external_integration_google_merchant",
    "external_integration_microsoft_advertising", "external_integration_google_search_console",
    "external_integration_google_bigquery",
]);

export function isCrmIntegrationResourceEvent(event) {
    const type = String(event?.entityType ?? "");
    return type.startsWith("project_ad_system_")
        || type.startsWith("external_integrations_host_name_")
        || type.startsWith("external_integration_") && /_(?:ad_account|customer_client|property|site|recipe|merchant_account|microsoft_customer)$/.test(type);
}

/** Returns only lifecycle transitions captured for this exact parent integration ID. */
export function integrationLifecycleEvents(integration, events = []) {
    if (!integration?.id) return [];
    const relatedResourceIds = new Set(uniqueIntegrationResources(integration.resources ?? []).map((resource) => resource.id).filter(Boolean));
    return events.filter((event) => {
        if (event?.source !== "crm") return false;
        const isIntegrationEvent = event.entityId === integration.id && INTEGRATION_LIFECYCLE_ENTITY_TYPES.has(event.entityType);
        const isRelatedResourceEvent = relatedResourceIds.has(event.entityId) && isCrmIntegrationResourceEvent(event);
        if (!isIntegrationEvent && !isRelatedResourceEvent) return false;
        if (isRelatedResourceEvent) return true;
        if (event.operation === "insert" || event.operation === "delete") return true;
        if (event.operation !== "update") return false;
        return event.before?.paused !== event.after?.paused && typeof event.before?.paused === "boolean" && typeof event.after?.paused === "boolean"
            || event.before?.authorization_successful !== event.after?.authorization_successful && typeof event.before?.authorization_successful === "boolean" && typeof event.after?.authorization_successful === "boolean"
            || ["external_integration_shoptet_addon", "external_integration_upgates_addon"].includes(event.entityType)
                && Object.hasOwn(event.before ?? {}, "status")
                && event.before?.status !== event.after?.status
                && ["active", "inactive", "paused"].includes(event.after?.status);
    });
}

export function mergeCrmEventPage(previous = null, result) {
    const existing = previous ?? {items: [], scanned: 0};
    const deduped = new Map((existing.items ?? []).map((event) => [`${event.source}:${event.id}`, event]));
    (result?.items ?? []).forEach((event) => deduped.set(`${event.source}:${event.id}`, event));
    const total = result?.total !== null && result?.total !== undefined && Number.isFinite(Number(result.total)) ? Number(result.total) : null;
    const scanned = total === null ? (existing.scanned ?? 0) + (result?.items?.length ?? 0) : Math.min(total, (existing.scanned ?? 0) + (result?.items?.length ?? 0));
    return {
        items: [...deduped.values()],
        scanned,
        total: total ?? existing.total ?? null,
        nextPage: (result?.page ?? 0) + 1,
        hasMore: total === null ? (result?.items?.length ?? 0) >= (result?.pageSize ?? 100) : scanned < total,
        loading: false,
        error: null,
    };
}

export function isCurrentIntegrationHistoryContext(submitted, current) {
    return submitted?.projectId === current?.projectId
        && submitted?.identityToken === current?.identityToken
        && submitted?.refreshKey === current?.refreshKey;
}

export function integrationLifecycleLabel(event, t) {
    if (event.operation === "insert") return t("admin.project_crm.lifecycle_created", {defaultValue: "Připojení vytvořeno"});
    if (event.operation === "delete") return t("admin.project_crm.lifecycle_removed", {defaultValue: "Odpojeno"});
    const labels = [];
    if (event.before?.paused !== event.after?.paused && typeof event.after?.paused === "boolean") {
        labels.push(event.after.paused
            ? t("admin.project_crm.lifecycle_paused", {defaultValue: "Pozastaveno"})
            : t("admin.project_crm.lifecycle_resumed", {defaultValue: "Znovu povoleno"}));
    }
    if (event.before?.authorization_successful !== event.after?.authorization_successful && typeof event.after?.authorization_successful === "boolean") {
        labels.push(event.after.authorization_successful
            ? t("admin.project_crm.lifecycle_authorization_restored", {defaultValue: "Autorizace obnovena"})
            : t("admin.project_crm.lifecycle_authorization_lost", {defaultValue: "Autorizace selhala"}));
    }
    if (event.before?.status !== event.after?.status && typeof event.after?.status === "string") {
        const statuses = {
            active: t("admin.project_crm.lifecycle_addon_active", {defaultValue: "Doplněk aktivován"}),
            paused: t("admin.project_crm.lifecycle_addon_paused", {defaultValue: "Doplněk pozastaven"}),
            inactive: t("admin.project_crm.lifecycle_addon_inactive", {defaultValue: "Doplněk deaktivován"}),
        };
        if (statuses[event.after.status]) labels.push(statuses[event.after.status]);
    }
    return labels.join(" · ") || null;
}

export function integrationTypeLabel(type, t, fallback = null) {
    switch (type) {
        case "meta_ads": return t("admin.project_crm.type_meta_ads", {defaultValue: "Meta Ads"});
        case "tiktok": return t("admin.project_crm.type_tiktok", {defaultValue: "TikTok Ads"});
        case "bs_shop": return t("admin.project_crm.type_bs_shop", {defaultValue: "BSSHOP"});
        case "criteo": return t("admin.project_crm.type_criteo", {defaultValue: "Criteo Ads"});
        case "sklik": return t("admin.project_crm.type_sklik", {defaultValue: "Sklik"});
        case "google_ads": return t("admin.project_crm.type_google_ads", {defaultValue: "Google Ads"});
        case "upgates": return t("admin.project_crm.type_upgates", {defaultValue: "Upgates"});
        case "google_analytics": return t("admin.project_crm.type_google_analytics", {defaultValue: "Google Analytics"});
        case "pinterest": return t("admin.project_crm.type_pinterest", {defaultValue: "Pinterest Ads"});
        case "mysql_leads": return t("admin.project_crm.type_mysql_leads", {defaultValue: "MySQL Leads"});
        case "zbozi": return t("admin.project_crm.type_zbozi", {defaultValue: "Zboží.cz"});
        case "upgates_addon": return t("admin.project_crm.type_upgates_addon", {defaultValue: "Upgates doplněk"});
        case "heureka": return t("admin.project_crm.type_heureka", {defaultValue: "Heureka"});
        case "glami": return t("admin.project_crm.type_glami", {defaultValue: "GLAMI"});
        case "shoptet_addon": return t("admin.project_crm.type_shoptet_addon", {defaultValue: "Shoptet doplněk"});
        case "shoptet": return t("admin.project_crm.type_shoptet", {defaultValue: "Shoptet"});
        case "google_merchant": return t("admin.project_crm.type_google_merchant", {defaultValue: "Google Merchant"});
        case "microsoft_advertising": return t("admin.project_crm.type_microsoft_advertising", {defaultValue: "Microsoft Advertising"});
        case "google_search_console": return t("admin.project_crm.type_google_search_console", {defaultValue: "Google Search Console"});
        case "google_bigquery": return t("admin.project_crm.type_google_bigquery", {defaultValue: "Google BigQuery"});
        default: {
            const isInternalIdentifier = (value) => typeof value === "string" && /^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(value);
            if (fallback && !isInternalIdentifier(fallback)) return fallback;
            if (type && !isInternalIdentifier(type)) return type;
            return t("admin.project_crm.type_other", {defaultValue: "Další integrace"});
        }
    }
}

export function contactRoleLabels(contact, deletedProject, t) {
    return {
        owner: deletedProject
            ? t("admin.project_crm.owner_at_deletion", {defaultValue: "Vlastník při smazání"})
            : t("admin.project_crm.owner", {defaultValue: "Vlastník"}),
        member: deletedProject
            ? t("admin.project_crm.member_at_deletion", {defaultValue: "Člen při smazání"})
            : t("admin.project_crm.member", {defaultValue: "Člen projektu"}),
        showOwner: contact?.isOwner === true,
        showMember: contact?.isMember === true,
    };
}

export function contactRoleSummary(contact, deletedProject, t) {
    if (contact?.isAdministrator === true) {
        return t("admin.project_crm.administrator", {defaultValue: "Administrátor"});
    }
    const roles = contactRoleLabels(contact, deletedProject, t);
    const isFormer = Boolean(contact?.removedAt) && !deletedProject;
    if (roles.showOwner) {
        return isFormer
            ? t("admin.project_crm.former_owner", {defaultValue: "Bývalý vlastník"})
            : roles.owner;
    }
    if (contact?.roleKnowledge === "known") {
        return typeof contact.roleName === "string" && contact.roleName.trim()
            ? contact.roleName.trim()
            : t("project.no-role", {defaultValue: "Bez role"});
    }
    if (contact?.roleKnowledge === "unknown") {
        return t("admin.project_crm.role_not_recorded", {defaultValue: "Role nezaznamenána"});
    }
    const labels = [];
    if (roles.showMember) {
        labels.push(isFormer
            ? t("admin.project_crm.former_member", {defaultValue: "Bývalý člen projektu"})
            : roles.member);
    }
    if (labels.length > 0) return labels.join(" · ");
    if (contact?.removedAt) {
        return t("admin.project_crm.former_contact", {defaultValue: "Dřívější kontakt"});
    }
    return t("admin.project_crm.project_contact", {defaultValue: "Kontakt projektu"});
}

export function crmContactRoleFilterKey(contact) {
    if (contact?.isOwner === true) return "owner";
    if (contact?.isAdministrator === true) return "administrator";
    if (contact?.roleKnowledge === "known") {
        return typeof contact.roleName === "string" && contact.roleName.trim()
            ? `role:${contact.roleName.trim()}`
            : "no-role";
    }
    if (contact?.roleKnowledge === "unknown") return "unknown";
    return contact?.isMember === true ? "member" : "unrecorded";
}

export function contactOriginLabel(origin, t) {
    if (origin === "observed") return t("admin.project_crm.origin_observed", {defaultValue: "Zaznamenán"});
    if (origin === "legacy_inferred") return t("admin.project_crm.origin_inferred_short", {defaultValue: "Historický odhad"});
    return t("admin.project_crm.origin_unknown", {defaultValue: "Původ neznámý"});
}

export function formatCrmDate(value, locale = "cs-CZ", {dateOnly = false} = {}) {
    if (!value) return null;
    const parsed = parseDate(value);
    if (!parsed) return null;
    const date = parsed.toDate();
    return new Intl.DateTimeFormat(locale, {
        dateStyle: "medium",
        ...(dateOnly ? {} : {timeStyle: "short"}),
        timeZone: "Europe/Prague",
    }).format(date);
}

export function formatCrmSmartDate(value, locale = "cs-CZ", t, {dateOnly = false, now = new Date()} = {}) {
    const parsed = parseDate(value);
    if (!parsed) return null;
    const today = moment.tz(now, TIME_ZONE).startOf("day");
    const dayDifference = parsed.clone().startOf("day").diff(today, "days");
    const relativeLabels = {
        [-2]: t?.("admin.project_crm.date_day_before_yesterday", {defaultValue: "Předevčírem"}) ?? "Předevčírem",
        [-1]: t?.("admin.project_crm.date_yesterday", {defaultValue: "Včera"}) ?? "Včera",
        [0]: t?.("admin.project_crm.date_today", {defaultValue: "Dnes"}) ?? "Dnes",
        [1]: t?.("admin.project_crm.date_tomorrow", {defaultValue: "Zítra"}) ?? "Zítra",
        [2]: t?.("admin.project_crm.date_day_after_tomorrow", {defaultValue: "Pozítří"}) ?? "Pozítří",
    };
    const includeYear = parsed.year() !== today.year();
    const absoluteDate = new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
        ...(includeYear ? {year: "numeric"} : {}),
        timeZone: TIME_ZONE,
    }).format(parsed.toDate());
    const label = relativeLabels[dayDifference] ?? absoluteDate;
    return dateOnly ? label : `${label} · ${parsed.format("HH:mm")}`;
}

export function groupCrmEventsByDay(events, locale = "cs-CZ") {
    const groups = [];
    for (const event of events ?? []) {
        const parsed = parseDate(event?.occurredAt);
        const dayKey = parsed ? parsed.format("YYYY-MM-DD") : `unknown:${event?.id ?? groups.length}`;
        const last = groups.at(-1);
        if (last?.key === dayKey) last.events.push(event);
        else groups.push({key: dayKey, label: parsed ? new Intl.DateTimeFormat(locale, {dateStyle: "full", timeZone: TIME_ZONE}).format(parsed.toDate()) : null, events: [event]});
    }
    return groups;
}

export function formatCrmTime(value, locale = "cs-CZ") {
    const parsed = parseDate(value);
    return parsed ? new Intl.DateTimeFormat(locale, {timeStyle: "short", timeZone: TIME_ZONE}).format(parsed.toDate()) : null;
}

export function primaryProjectContact(project) {
    if (project?.primaryContact && typeof project.primaryContact === "object") {
        const contact = project.primaryContact;
        if (![contact.name, contact.email, contact.phone].some((value) => typeof value === "string" && value.trim())) return null;
        const fullContact = (project?.contacts ?? []).find((item) => item.userId === contact.userId);
        return {...fullContact, ...contact, origin: contact.contactOrigin ?? "unknown"};
    }
    if (!project?.primaryContactUserId) return null;
    const contact = (project?.contacts ?? []).find((item) => item.userId === project.primaryContactUserId && !item.isAdministrator);
    if (!contact) return null;
    return {
        ...contact,
        origin: contact.contactOrigin ?? project.primaryContactOrigin ?? "unknown",
    };
}

export function canSelectCrmPrimaryContact(contact, currentPrimaryUserId) {
    return Boolean(contact?.userId) && contact.isAdministrator !== true && contact.userId !== currentPrimaryUserId;
}

const EVENT_FIELD_KEYS = {
    contactName: ["contactName", "contact_name"],
    name: ["name", "display_name", "displayName", "host_name", "hostName", "recipe_name", "recipeName"],
    currency: ["currency", "currency_code", "currencyCode"],
    createdAt: ["createdAt", "created", "created_at"],
    deletedAt: ["deletedAt", "deleted_at"],
    isDeleted: ["isDeleted", "is_deleted", "projectDeleted"],
    companyName: ["companyName", "company_name"],
    street: ["street"],
    city: ["city"],
    zip: ["zip"],
    email: ["email"],
    countryCode: ["countryCode", "country_code"],
    companyRegistrationNumber: ["companyRegistrationNumber", "company_registration_number"],
    vatNumber: ["vatNumber", "vat_number"],
    packageName: ["packageName", "package_name"],
    nextPackageName: ["nextPackageName", "next_package_name", "targetPackageName", "target_package_name"],
    status: ["status"],
    billingCycle: ["billingCycle", "billing_cycle"],
    nextBillingCycle: ["nextBillingCycle", "next_billing_cycle", "targetBillingCycle", "target_billing_cycle"],
    startDate: ["startDate", "start_date"],
    endDate: ["endDate", "end_date"],
    isFreeTrial: ["isFreeTrial", "is_free_trial"],
    cancelAtPeriodEnd: ["cancelAtPeriodEnd", "cancel_at_period_end"],
    paymentFailedAt: ["paymentFailedAt", "payment_failed_at"],
    paymentGraceEndsAt: ["paymentGraceEndsAt", "payment_grace_ends_at"],
    paidAt: ["paidAt", "paid_at", "effectiveAt", "effective_at"],
    phone: ["phone"],
    enabled: ["enabled"],
    paused: ["paused"],
    authorizationSuccessful: ["authorizationSuccessful", "authorization_successful"],
    crmStatus: ["crmStatus", "crm_status"],
    nextContactAt: ["nextContactAt", "next_contact_at"],
    primaryContactUserId: ["primaryContactUserId", "primary_contact_user_id"],
    isOwner: ["isOwner", "is_owner"],
    isAdministrator: ["isAdministrator", "is_administrator", "isAdmin", "is_admin"],
    firstName: ["first_name", "firstName"],
    lastName: ["last_name", "lastName"],
    body: ["body"],
    isMember: ["isMember", "is_member"],
};

const PROJECT_EVENT_FIELDS = ["name", "currency", "createdAt", "deletedAt", "isDeleted"];
const BILLING_EVENT_FIELDS = ["companyName", "street", "city", "zip", "email", "countryCode", "companyRegistrationNumber", "vatNumber"];
const SUBSCRIPTION_EVENT_FIELDS = ["packageName", "nextPackageName", "status", "billingCycle", "nextBillingCycle", "startDate", "endDate", "isFreeTrial", "cancelAtPeriodEnd", "paymentFailedAt", "paymentGraceEndsAt", "paidAt"];
const INTEGRATION_EVENT_FIELDS = ["name", "type", "createdAt", "authorizationSuccessful", "paused", "status"];
const RESOURCE_EVENT_FIELDS = ["name", "enabled", "authorizationSuccessful", "paused", "status"];

function getEventValue(source, field) {
    for (const key of EVENT_FIELD_KEYS[field] ?? []) {
        if (Object.hasOwn(source ?? {}, key)) return source[key];
    }
    return undefined;
}

function eventFieldSpecs(entityType) {
    if (entityType === "opportunity") return ["name", "contactName", "email", "phone", "crmStatus", "nextContactAt"];
    if (entityType === "project") return [...PROJECT_EVENT_FIELDS, ...BILLING_EVENT_FIELDS.map((field) => `billing.${field}`), ...SUBSCRIPTION_EVENT_FIELDS.map((field) => `subscription.${field}`)];
    if (["billing_account", "billingAccount"].includes(entityType)) return BILLING_EVENT_FIELDS;
    if (["project_subscription", "subscription"].includes(entityType)) return SUBSCRIPTION_EVENT_FIELDS;
    if (["project_user", "membership"].includes(entityType)) return ["isOwner", "isAdministrator"];
    if (["user", "project_user_profile", "project_contact"].includes(entityType)) return ["name", "email", "phone", "isOwner", "isMember"];
    if (["crm_project", "crm_project_record"].includes(entityType)) return ["crmStatus", "nextContactAt", "primaryContactUserId", "isDeleted", "deletedAt"];
    if (["note", "crm_project_note"].includes(entityType)) return ["body"];
    if (entityType === "project_subscription_packages") return ["name"];
    if (String(entityType ?? "").startsWith("external_integration_") && /_(?:ad_account|customer_client|property|site|recipe|merchant_account|microsoft_customer)$/.test(entityType)) return RESOURCE_EVENT_FIELDS;
    if (["project_ad_system_ad_account", "external_integrations_host_name_and_csv_link", "external_integrations_host_name_and_tld_and_currency_code_and_a", "external_integrations_host_name_and_xml_link", "external_integrations_host_name_api_id_api_key"].includes(entityType)) return RESOURCE_EVENT_FIELDS;
    if (String(entityType ?? "").startsWith("external_integration_")) return INTEGRATION_EVENT_FIELDS;
    return [];
}

function readEventField(source, field) {
    if (field.startsWith("billing.")) return getEventValue(source?.billing, field.slice("billing.".length));
    if (field.startsWith("subscription.")) return getEventValue(source?.subscription, field.slice("subscription.".length));
    if (field === "name" && (Object.hasOwn(source ?? {}, "first_name") || Object.hasOwn(source ?? {}, "firstName"))) {
        const first = getEventValue(source, "firstName") ?? "";
        const last = getEventValue(source, "lastName") ?? "";
        return `${first} ${last}`.trim();
    }
    return getEventValue(source, field);
}

function displayEventValue(field, value, locale, t, context) {
    if (value === null || value === undefined || value === "") return t("admin.project_crm.event_value_unknown", {defaultValue: "Neznámé"});
    const simpleField = field.split(".").at(-1);
    if (simpleField === "primaryContactUserId") {
        const contact = context.contacts?.find((item) => item.userId === value);
        return contact?.name || t("admin.project_crm.event_contact_unavailable", {defaultValue: "Kontakt není v dostupném přehledu"});
    }
    if (["crmStatus", "crm_status"].includes(simpleField)) return crmStatusLabel(value, t);
    if (simpleField === "isDeleted") return value ? t("yes", {defaultValue: "Ano"}) : t("no", {defaultValue: "Ne"});
    if (simpleField === "status" && (["project_subscription", "subscription"].includes(context.entityType) || field.startsWith("subscription."))) return subscriptionStatusLabel(value, t);
    if (["billingCycle", "nextBillingCycle", "billing_cycle", "next_billing_cycle"].includes(simpleField)) return subscriptionBillingCycleLabel(value, t);
    if (["type", "integrationType"].includes(simpleField)) return integrationTypeLabel(value, t, t("admin.project_crm.event_value_unknown", {defaultValue: "Neznámé"}));
    if (typeof value === "boolean") return value ? t("yes", {defaultValue: "Ano"}) : t("no", {defaultValue: "Ne"});
    if (typeof value === "number") return new Intl.NumberFormat(locale).format(value);
    if (typeof value !== "string") return t("admin.project_crm.event_value_unavailable", {defaultValue: "Detail není k dispozici"});
    if (simpleField === "currency") return value.toUpperCase();
    if (/At$|Date$|^createdAt$|^created$/.test(simpleField) && parseDate(value)) return formatCrmDate(value, locale) ?? t("admin.project_crm.event_value_unknown", {defaultValue: "Neznámé"});
    if (simpleField === "status" && context.entityType?.startsWith("external_integration_")) {
        if (["external_integration_shoptet_addon", "external_integration_upgates_addon"].includes(context.entityType)) {
            const addonStatuses = {
                active: t("admin.project_crm.lifecycle_addon_active", {defaultValue: "Doplněk aktivován"}),
                inactive: t("admin.project_crm.integration_deactivated", {defaultValue: "Doplněk deaktivován"}),
                paused: t("admin.project_crm.lifecycle_addon_paused", {defaultValue: "Doplněk pozastaven"}),
            };
            return addonStatuses[value.toLocaleLowerCase()] ?? t("admin.project_crm.event_value_unknown", {defaultValue: "Neznámé"});
        }
        const integrationStatuses = {
            active: t("admin.project_crm.integration_ready", {defaultValue: "Připraveno"}),
            inactive: t("admin.project_crm.integration_inactive", {defaultValue: "Neaktivní"}),
            paused: t("admin.project_crm.integration_paused", {defaultValue: "Pozastaveno"}),
            disconnected: t("admin.project_crm.integration_removed", {defaultValue: "Odpojeno"}),
        };
        return integrationStatuses[value.toLocaleLowerCase()] ?? t("admin.project_crm.event_value_unknown", {defaultValue: "Neznámé"});
    }
    return value;
}

export function isCrmBaselineEvent(event) {
    return String(event?.operation ?? "").toLowerCase() === "baseline" && event?.entityType === "project";
}

export function eventSubjectDetails(event, contacts = [], t = (key, options) => options?.defaultValue ?? key) {
    const snapshots = [event?.after, event?.before];
    if (["project", "project_contact", "billing_account"].includes(event?.entityType)) {
        for (const snapshot of snapshots) {
            const name = snapshot?.name ?? snapshot?.company_name ?? snapshot?.companyName;
            if (typeof name === "string" && name.trim()) return {label: name};
        }
    }
    if (["project_contact", "project_user", "membership", "user", "project_user_profile"].includes(event?.entityType)) {
        let membershipUserId = null;
        for (const snapshot of snapshots) {
            const userId = snapshot?.user_id ?? snapshot?.userId ?? (["user", "project_contact", "project_user_profile"].includes(event?.entityType) ? snapshot?.id : null);
            if (["project_user", "membership"].includes(event?.entityType) && typeof userId === "string" && userId.trim() && membershipUserId === null) membershipUserId = userId.trim();
            const contact = contacts.find((item) => item.userId === userId);
            if (contact?.name) return {label: contact.name};
            const name = [snapshot?.first_name ?? snapshot?.firstName, snapshot?.last_name ?? snapshot?.lastName].filter(Boolean).join(" ");
            if (name) return {label: name};
        }
        if (membershipUserId) {
            const shortId = membershipUserId.length > 16 ? `${membershipUserId.slice(0, 8)}…${membershipUserId.slice(-4)}` : membershipUserId;
            return {label: t("admin.project_crm.event_user_id_subject", {id: shortId, defaultValue: "Uživatel {{id}}"}), title: membershipUserId};
        }
    }
    if (String(event?.entityType ?? "").match(/_(?:ad_account|customer_client|property|site|recipe|merchant_account|microsoft_customer)$/) || String(event?.entityType ?? "").startsWith("project_ad_system_") || String(event?.entityType ?? "").startsWith("external_integrations_host_name_")) {
        for (const snapshot of snapshots) {
            const name = snapshot?.name ?? snapshot?.display_name ?? snapshot?.displayName ?? snapshot?.host_name ?? snapshot?.hostName ?? snapshot?.recipe_name ?? snapshot?.recipeName;
            if (typeof name === "string" && name.trim()) return {label: name};
        }
    }
    return null;
}

export function eventSubjectLabel(event, contacts = [], t) {
    return eventSubjectDetails(event, contacts, t)?.label ?? null;
}

export function eventEmptyDescription(event, t, subject = null) {
    if (["user", "project_contact", "project_user_profile"].includes(event?.entityType)) return t("admin.project_crm.event_contact_no_details", {defaultValue: "Bez změny kontaktních údajů."});
    if (["project_user", "membership"].includes(event?.entityType)) {
        if (subject) return null;
        return t("admin.project_crm.event_membership_no_details", {defaultValue: "Osobu se nepodařilo přiřadit ke kontaktu projektu."});
    }
    return t("admin.project_crm.no_field_diff", {defaultValue: "Pro tuto událost nejsou dostupné detailní hodnoty."});
}

export function eventActorLabel(actor, t) {
    if (actor?.type === "system") return t("admin.project_crm.actor_system", {defaultValue: "Automatický proces"});
    if (!["user", "admin"].includes(actor?.type) || typeof actor?.name !== "string" || !actor.name.trim()) {
        return t("admin.project_crm.actor_not_recorded", {defaultValue: "Autor nezaznamenán"});
    }
    const actorName = actor.name.trim();
    return actor.type === "admin"
        ? `${actorName} · ${t("admin.project_crm.actor_administrator", {defaultValue: "Administrátor"})}`
        : actorName;
}

export function buildCrmHistoryPeople(contacts = [], events = [], notes = []) {
    const peopleById = new Map();
    const add = (userId, name) => {
        if (typeof userId !== "string" || !userId.trim() || typeof name !== "string" || !name.trim()) return;
        const id = userId.trim();
        if (!peopleById.has(id)) peopleById.set(id, {userId: id, name: name.trim()});
    };
    for (const contact of contacts) add(contact?.userId, contact?.name);
    for (const event of events) {
        const actor = event?.actor;
        if (["user", "admin"].includes(actor?.type)) add(actor.userId, actor.name);
    }
    for (const note of notes) add(note?.authorUserId, note?.authorName);
    return [...peopleById.values()];
}

export function hasRecordedEventActor(actor) {
    return actor?.type === "system" || (["user", "admin"].includes(actor?.type) && typeof actor?.name === "string" && Boolean(actor.name.trim()));
}

export function hasUnrecordedEventActor(events = []) {
    return events.some((event) => !hasRecordedEventActor(event?.actor));
}

function normalizedSnapshotRow(field, value, locale, t) {
    if (value === null || value === undefined || value === "") return null;
    const label = eventFieldLabel(field, t);
    let display = value;
    if (field === "currency") display = String(value).toUpperCase();
    else if (field === "status") display = subscriptionStatusLabel(value, t);
    else if (field === "billingCycle") display = subscriptionBillingCycleLabel(value, t);
    else if (field === "isFreeTrial" || field === "cancelAtPeriodEnd") display = value ? t("yes", {defaultValue: "Ano"}) : t("no", {defaultValue: "Ne"});
    else if (["createdAt", "startDate", "endDate", "removedAt"].includes(field)) display = formatCrmDate(value, locale, {dateOnly: ["startDate", "endDate"].includes(field)}) ?? String(value);
    else if (field === "packageNetPrice") display = formatSubscriptionNetPrice({packageNetPrice: value, packageCurrency: null, billingCycle: "unknown"}, locale, t);
    return {field, label, value: String(display)};
}

function snapshotRows(source, fields, locale, t) {
    return fields.map((field) => normalizedSnapshotRow(field, getEventValue(source, field), locale, t)).filter(Boolean);
}

export function baselineSnapshotSections(snapshot, locale = "cs-CZ", t = (key) => key) {
    if (!snapshot || typeof snapshot !== "object" || Array.isArray(snapshot)) return [];
    const sections = [];
    const projectRows = snapshotRows(snapshot, ["name", "currency", "createdAt"], locale, t);
    if (snapshot.projectDeleted === true) projectRows.push({field: "projectDeleted", label: eventFieldLabel("projectDeleted", t), value: t("yes", {defaultValue: "Ano"})});
    if (projectRows.length) sections.push({key: "project", title: t("admin.project_crm.baseline_project", {defaultValue: "Projekt"}), rows: projectRows});

    const billingRows = snapshotRows(snapshot.billing, ["companyName", "street", "city", "zip", "email", "countryCode", "companyRegistrationNumber", "vatNumber"], locale, t);
    if (billingRows.length) sections.push({key: "billing", title: t("admin.project_crm.baseline_billing", {defaultValue: "Fakturace"}), rows: billingRows});

    const contacts = (Array.isArray(snapshot.contacts) ? snapshot.contacts : []).map((contact) => ({
        name: contact?.name || t("admin.project_crm.unknown_contact", {defaultValue: "Neznámý kontakt"}),
        rows: snapshotRows(contact, ["phone", "email", "removedAt"], locale, t),
    }));
    if (Array.isArray(snapshot.contacts)) sections.push({key: "contacts", title: t("admin.project_crm.baseline_contacts", {defaultValue: "Kontakty"}), contacts});

    const integrations = sortCrmIntegrations(Array.isArray(snapshot.integrations) ? snapshot.integrations : []).map((integration) => {
        const state = integrationDisplayState(integration);
        const allResources = uniqueIntegrationResources(Array.isArray(integration?.resources) ? integration.resources : []);
        const visibleResources = allResources.filter((resource) => !isTechnicalIntegrationResource(resource) && (!isSelectableIntegrationAccount(resource) || resourcePresentationState(resource) === "enabled"));
        const resources = visibleResources.map((resource) => {
            const resourceState = resourcePresentationState(resource);
            return {
                name: resource?.name || t("admin.project_crm.resource_name_unknown", {defaultValue: "Zdroj bez názvu"}),
                state: resourceState === "enabled"
                    ? t("admin.project_crm.resource_enabled", {defaultValue: "Zapnuto"})
                    : resourceState === "disabled"
                        ? t("admin.project_crm.resource_disabled", {defaultValue: "Vypnuto"})
                        : resourceState === "removed"
                            ? t("admin.project_crm.integration_removed", {defaultValue: "Odpojeno"})
                            : t("admin.project_crm.resource_unknown", {defaultValue: "Stav neznámý"}),
            };
        }).filter(Boolean);
        return {
            name: integrationTypeLabel(integration?.type, t, integration?.name || null),
            state: integrationPresentationLabel(state, t),
            resources,
            additionalResourceCount: allResources.length - visibleResources.length,
        };
    });
    if (Array.isArray(snapshot.integrations)) sections.push({key: "integrations", title: t("admin.project_crm.baseline_integrations", {defaultValue: "Integrace"}), integrations});

    if (snapshot.subscription) {
        const subscription = snapshot.subscription;
        const rows = snapshotRows(subscription, ["status", "billingCycle", "startDate", "endDate", "isFreeTrial"], locale, t);
    if (subscription.packageNetPrice !== null && subscription.packageNetPrice !== undefined && subscription.packageCurrency) rows.push({field: "packageNetPrice", label: eventFieldLabel("packageNetPrice", t), value: formatSubscriptionNetPrice(subscription, locale, t)});
        sections.push({
            key: "subscription",
            title: t("admin.project_crm.baseline_subscription", {defaultValue: "Předplatné"}),
            rows: [{field: "packageName", label: eventFieldLabel("packageName", t), value: subscription.packageName || t("admin.project_crm.event_value_unknown", {defaultValue: "Neznámé"})}, ...rows],
        });
    } else if (snapshot.subscription === null) {
        sections.push({key: "subscription", title: t("admin.project_crm.baseline_subscription", {defaultValue: "Předplatné"}), rows: [{field: "subscription", label: t("admin.project_crm.baseline_subscription", {defaultValue: "Předplatné"}), value: t("no_subscription", {defaultValue: "Bez předplatného"})}]});
    }
    return sections;
}

export function eventFieldChanges(before, after, locale = "cs-CZ", t = (key) => key, {entityType = null, contacts = []} = {}) {
    const fields = eventFieldSpecs(entityType);
    const previous = before ?? {};
    const current = after ?? {};
    const normalize = (value) => value === null || value === undefined || value === "" ? null : value;
    return fields.map((field) => {
        const oldValue = readEventField(previous, field);
        const newValue = readEventField(current, field);
        if (JSON.stringify(normalize(oldValue)) === JSON.stringify(normalize(newValue))) return null;
        if (["project_user", "membership"].includes(entityType) && field === "isOwner" && oldValue !== true && newValue !== true) return null;
        const context = {entityType, contacts};
        const missing = t("admin.project_crm.event_value_missing", {defaultValue: "—"});
        return {
            field,
            before: oldValue === undefined || oldValue === null || oldValue === "" ? missing : displayEventValue(field, oldValue, locale, t, context),
            after: newValue === undefined || newValue === null || newValue === "" ? missing : displayEventValue(field, newValue, locale, t, context),
        };
    }).filter(Boolean);
}

export function eventFieldLabel(field, t) {
    const labels = {
        crmStatus: t("admin.project_crm.field_crm_status", {defaultValue: "CRM stav"}),
        nextContactAt: t("admin.project_crm.field_next_contact", {defaultValue: "Další kontakt"}),
        contactName: t("admin.project_crm.contact_name", {defaultValue:"Jméno kontaktu"}),
        name: t("admin.project_crm.field_name", {defaultValue: "Název"}),
        currency: t("admin.project_crm.field_currency", {defaultValue: "Měna"}),
        companyName: t("admin.project_crm.field_company", {defaultValue: "Firma"}),
        email: t("admin.project_crm.field_email", {defaultValue: "E-mail"}),
        phone: t("admin.project_crm.field_phone", {defaultValue: "Telefon"}),
        street: t("admin.project_crm.field_street", {defaultValue: "Ulice"}),
        city: t("admin.project_crm.field_city", {defaultValue: "Město"}),
        zip: t("admin.project_crm.field_zip", {defaultValue: "PSČ"}),
        countryCode: t("admin.project_crm.field_country", {defaultValue: "Země"}),
        packageName: t("admin.project_crm.field_package", {defaultValue: "Balíček"}),
        status: t("admin.project_crm.field_status", {defaultValue: "Stav"}),
        endDate: t("admin.project_crm.field_end_date", {defaultValue: "Konec předplatného"}),
        deletedAt: t("admin.project_crm.field_deleted_at", {defaultValue: "Datum smazání"}),
        billingCycle: t("admin.project_crm.field_billing_cycle", {defaultValue: "Fakturační období"}),
        createdAt: t("admin.project_crm.field_created_at", {defaultValue: "Datum vytvoření"}),
        updatedAt: t("admin.project_crm.field_updated_at", {defaultValue: "Datum aktualizace"}),
        isDeleted: t("admin.project_crm.field_lifecycle", {defaultValue: "Životní cyklus projektu"}),
        removedAt: t("admin.project_crm.member_removed_at", {defaultValue: "Odebrán z projektu"}),
        userId: t("admin.project_crm.field_contact", {defaultValue: "Kontakt"}),
        projectId: t("admin.project_crm.field_project_id", {defaultValue: "ID projektu"}),
        remoteId: t("admin.project_crm.field_remote_id", {defaultValue: "ID u poskytovatele"}),
        enabled: t("admin.project_crm.field_enabled", {defaultValue: "Povoleno"}),
        paused: t("admin.project_crm.field_paused", {defaultValue: "Pozastaveno"}),
        type: t("admin.project_crm.field_type", {defaultValue: "Typ"}),
        companyRegistrationNumber: t("admin.project_crm.field_company_number", {defaultValue: "IČO"}),
        vatNumber: t("admin.project_crm.field_vat_number", {defaultValue: "DIČ"}),
        isOwner: t("admin.project_crm.field_owner", {defaultValue: "Vlastník"}),
        isAdministrator: t("admin.project_crm.field_administrator", {defaultValue: "Administrátor"}),
        isMember: t("admin.project_crm.field_member", {defaultValue: "Člen projektu"}),
        contactOrigin: t("admin.project_crm.field_contact_origin", {defaultValue: "Původ kontaktu"}),
        primaryContactOrigin: t("admin.project_crm.field_contact_origin", {defaultValue: "Původ hlavního kontaktu"}),
        billingAccountId: t("admin.project_crm.field_billing_account", {defaultValue: "Fakturační účet"}),
        isPrimaryContact: t("admin.project_crm.field_primary_contact", {defaultValue: "Hlavní kontakt"}),
        authorizationSuccessful: t("admin.project_crm.field_authorization", {defaultValue: "Úspěšnost autorizace"}),
        billing_account_id: t("admin.project_crm.field_billing_account", {defaultValue: "Fakturační účet"}),
        created_at: t("admin.project_crm.field_created_at", {defaultValue: "Datum vytvoření"}),
        project_created_at: t("admin.project_crm.field_created_at", {defaultValue: "Datum vytvoření projektu"}),
        deleted_at: t("admin.project_crm.field_deleted_at", {defaultValue: "Datum smazání"}),
        next_contact_at: t("admin.project_crm.field_next_contact", {defaultValue: "Další kontakt"}),
        crm_status: t("admin.project_crm.field_crm_status", {defaultValue: "CRM stav"}),
        primary_contact_user_id: t("admin.project_crm.field_primary_contact", {defaultValue: "Hlavní kontakt"}),
        primaryContactUserId: t("admin.project_crm.field_primary_contact", {defaultValue: "Hlavní kontakt"}),
        authorization_successful: t("admin.project_crm.field_authorization", {defaultValue: "Autorizace úspěšná"}),
        is_deleted: t("admin.project_crm.field_lifecycle", {defaultValue: "Smazaný projekt"}),
        package_code: t("admin.project_crm.field_package", {defaultValue: "Kód balíčku"}),
        package_name: t("admin.project_crm.field_package", {defaultValue: "Balíček"}),
        start_date: t("admin.project_crm.field_start_date", {defaultValue: "Začátek předplatného"}),
        end_date: t("admin.project_crm.field_end_date", {defaultValue: "Konec předplatného"}),
        billing_cycle: t("admin.project_crm.field_billing_cycle", {defaultValue: "Fakturační období"}),
        company_registration_number: t("admin.project_crm.field_company_number", {defaultValue: "IČO"}),
        vat_number: t("admin.project_crm.field_vat_number", {defaultValue: "DIČ"}),
        is_owner: t("admin.project_crm.field_owner", {defaultValue: "Vlastník"}),
        is_member: t("admin.project_crm.field_member", {defaultValue: "Člen projektu"}),
        contact_origin: t("admin.project_crm.field_contact_origin", {defaultValue: "Původ kontaktu"}),
        primary_contact_origin: t("admin.project_crm.field_contact_origin", {defaultValue: "Původ hlavního kontaktu"}),
        company_name: t("admin.project_crm.field_company", {defaultValue: "Firma"}),
        country_code: t("admin.project_crm.field_country", {defaultValue: "Země"}),
        next_package_name: t("admin.project_crm.field_next_package", {defaultValue: "Následující balíček"}),
        nextPackageName: t("admin.project_crm.field_next_package", {defaultValue: "Následující balíček"}),
        nextBillingCycle: t("admin.project_crm.field_next_billing_cycle", {defaultValue: "Následující fakturační období"}),
        next_billing_cycle: t("admin.project_crm.field_next_billing_cycle", {defaultValue: "Následující fakturační období"}),
        is_free_trial: t("admin.project_crm.field_free_trial", {defaultValue: "Zkušební verze"}),
        cancel_at_period_end: t("admin.project_crm.field_cancel_at_period_end", {defaultValue: "Zrušení na konci období"}),
        packageNetPrice: t("admin.project_crm.field_package_price", {defaultValue: "Cena balíčku"}),
        projectDeleted: t("admin.project_crm.field_lifecycle", {defaultValue: "Životní cyklus projektu"}),
        firstName: t("admin.project_crm.field_name", {defaultValue: "Název"}),
        is_primary_contact: t("admin.project_crm.field_primary_contact", {defaultValue: "Hlavní kontakt"}),
        first_name: t("admin.project_crm.field_name", {defaultValue: "Název"}),
        last_name: t("admin.project_crm.field_name", {defaultValue: "Název"}),
        paymentFailedAt: t("admin.project_crm.field_payment_failed_at", {defaultValue: "Platba selhala"}),
        payment_failed_at: t("admin.project_crm.field_payment_failed_at", {defaultValue: "Platba selhala"}),
        paymentGraceEndsAt: t("admin.project_crm.field_payment_grace_ends_at", {defaultValue: "Konec ochranné lhůty"}),
        payment_grace_ends_at: t("admin.project_crm.field_payment_grace_ends_at", {defaultValue: "Konec ochranné lhůty"}),
        paidAt: t("admin.project_crm.field_paid_at", {defaultValue: "Zaplaceno"}),
        paid_at: t("admin.project_crm.field_paid_at", {defaultValue: "Zaplaceno"}),
        effectiveAt: t("admin.project_crm.field_effective_at", {defaultValue: "Platí od"}),
        effective_at: t("admin.project_crm.field_effective_at", {defaultValue: "Platí od"}),
        startDate: t("admin.project_crm.field_start_date", {defaultValue: "Začátek předplatného"}),
        cancelAtPeriodEnd: t("admin.project_crm.field_cancel_at_period_end", {defaultValue: "Zrušení na konci období"}),
        isFreeTrial: t("admin.project_crm.field_free_trial", {defaultValue: "Zkušební verze"}),
        body: t("admin.project_crm.field_note_body", {defaultValue: "Poznámka"}),
    };
    const segments = field.split(".");
    const last = segments.at(-1);
    return labels[last] ?? last.replace(/([A-Z])/g, " $1").replace(/[_-]+/g, " ").replace(/^./, (letter) => letter.toLocaleUpperCase());
}

export function eventEntityLabel(entityType, t) {
    const labels = {
        opportunity: t("admin.project_crm.entity_opportunity", {defaultValue:"Příležitost"}),
        project: t("admin.project_crm.entity_project", {defaultValue: "Projekt"}),
        crm_project_record: t("admin.project_crm.entity_project_record", {defaultValue: "Historie projektu"}),
        project_configuration: t("admin.project_crm.entity_project", {defaultValue: "Projekt"}),
        billing_account: t("admin.project_crm.entity_billing_account", {defaultValue: "Fakturační účet"}),
        project_contact: t("admin.project_crm.entity_contact", {defaultValue: "Kontakt projektu"}),
        project_user: t("admin.project_crm.entity_contact", {defaultValue: "Kontakt projektu"}),
        integration: t("admin.project_crm.entity_integration", {defaultValue: "Integrace"}),
        integration_resource: t("admin.project_crm.entity_resource", {defaultValue: "Zdroj integrace"}),
        project_ad_system_ad_account: t("admin.project_crm.entity_resource", {defaultValue: "Zdroj integrace"}),
        external_integrations_host_name_and_csv_link: t("admin.project_crm.entity_resource", {defaultValue: "Zdroj integrace"}),
        external_integrations_host_name_and_tld_and_currency_code_and_a: t("admin.project_crm.entity_resource", {defaultValue: "Zdroj integrace"}),
        external_integrations_host_name_and_xml_link: t("admin.project_crm.entity_resource", {defaultValue: "Zdroj integrace"}),
        external_integrations_host_name_api_id_api_key: t("admin.project_crm.entity_resource", {defaultValue: "Zdroj integrace"}),
        subscription: t("admin.project_crm.entity_subscription", {defaultValue: "Předplatné"}),
        project_subscription: t("admin.project_crm.entity_subscription", {defaultValue: "Předplatné"}),
        project_subscription_packages: t("admin.project_crm.field_package", {defaultValue: "Balíček"}),
        membership: t("admin.project_crm.entity_membership", {defaultValue: "Členství"}),
        user: t("admin.project_crm.entity_user", {defaultValue: "Uživatelský účet"}),
        project_user_profile: t("admin.project_crm.entity_user", {defaultValue: "Uživatelský účet"}),
        crm_project_note: t("admin.project_crm.entity_note", {defaultValue: "CRM poznámka"}),
        meta_ads_ad_account: t("admin.project_crm.entity_meta_ads_ad_account", {defaultValue: "Účet Meta Ads"}),
        google_ads_customer: t("admin.project_crm.entity_google_ads_customer", {defaultValue: "Účet Google Ads"}),
    };
    if (String(entityType ?? "").startsWith("external_integration_") && /_(?:ad_account|customer_client|property|site|recipe|merchant_account|microsoft_customer)$/.test(entityType)) {
        return t("admin.project_crm.entity_resource", {defaultValue: "Zdroj integrace"});
    }
    if (entityType?.startsWith("external_integration_")) {
        const providerType = entityType.slice("external_integration_".length).replace(/_resources?$/, "");
        const knownProviders = ["meta_ads", "tiktok", "bs_shop", "criteo", "sklik", "google_ads", "upgates", "google_analytics", "pinterest", "mysql_leads", "zbozi", "upgates_addon", "heureka", "glami", "shoptet_addon", "shoptet", "google_merchant", "microsoft_advertising", "google_search_console", "google_bigquery"];
        if (!knownProviders.includes(providerType)) return t("admin.project_crm.event_entity_unknown", {defaultValue: "Změna"});
        return t("admin.project_crm.entity_integration_provider", {provider: integrationTypeLabel(providerType, t), defaultValue: "Integrace {{provider}}"});
    }
    return labels[entityType] ?? t("admin.project_crm.event_entity_unknown", {defaultValue: "Změna"});
}

export function eventOperationLabel(operation, t, entityType = null) {
    const labels = {
        insert: t("admin.project_crm.operation_insert", {defaultValue: "vytvořeno"}),
        update: t("admin.project_crm.operation_update", {defaultValue: "upraveno"}),
        delete: t("admin.project_crm.operation_delete", {defaultValue: "smazáno"}),
        snapshot: t("admin.project_crm.operation_snapshot", {defaultValue: "zachyceno"}),
    };
    const normalizedOperation = String(operation ?? "").toLowerCase();
    if (["project_user", "membership"].includes(entityType)) {
        const membershipLabels = {
            insert: t("admin.project_crm.operation_member_added", {defaultValue: "přidán do projektu"}),
            update: t("admin.project_crm.operation_member_updated", {defaultValue: "členství upraveno"}),
            delete: t("admin.project_crm.operation_member_removed", {defaultValue: "odebrán z projektu"}),
        };
        if (membershipLabels[normalizedOperation]) return membershipLabels[normalizedOperation];
    }
    const isIntegrationResource = String(entityType ?? "").match(/_(?:ad_account|customer_client|property|site|recipe|merchant_account|microsoft_customer)$/) || String(entityType ?? "").startsWith("project_ad_system_") || String(entityType ?? "").startsWith("external_integrations_host_name_");
    if (String(entityType ?? "").startsWith("external_integration_") && !isIntegrationResource) {
        const integrationLabels = {
            insert: t("admin.project_crm.operation_integration_connected", {defaultValue: "připojeno"}),
            update: t("admin.project_crm.operation_integration_updated", {defaultValue: "stav změněn"}),
            delete: t("admin.project_crm.operation_integration_removed", {defaultValue: "odpojeno"}),
        };
        if (integrationLabels[normalizedOperation]) return integrationLabels[normalizedOperation];
    }
    if (isIntegrationResource) {
        const resourceLabels = {
            insert: t("admin.project_crm.operation_resource_added", {defaultValue: "zdroj přidán"}),
            update: t("admin.project_crm.operation_resource_updated", {defaultValue: "zdroj upraven"}),
            delete: t("admin.project_crm.operation_resource_removed", {defaultValue: "zdroj odebrán"}),
        };
        if (resourceLabels[normalizedOperation]) return resourceLabels[normalizedOperation];
    }
    return labels[normalizedOperation] ?? t("admin.project_crm.operation_unknown", {defaultValue: "upraveno"});
}

export function eventHeadingLabel(event, changes, t) {
    const operation = String(event?.operation ?? "").toLowerCase();
    const fields = (changes ?? []).map(({field}) => field);
    const before = event?.before ?? {};
    const after = event?.after ?? {};
    const beforeDeleted = before.is_deleted ?? before.isDeleted;
    const afterDeleted = after.is_deleted ?? after.isDeleted;
    const beforeAdmin = before.is_administrator ?? before.isAdministrator ?? before.is_admin ?? before.isAdmin;
    const afterAdmin = after.is_administrator ?? after.isAdministrator ?? after.is_admin ?? after.isAdmin;
    const adminMembership = beforeAdmin === true || afterAdmin === true;
    const membershipType = event?.entityType === "project_user" || event?.entityType === "membership";
    if (membershipType) {
        if (adminMembership) {
            if (beforeAdmin !== true && afterAdmin === true) return t("admin.project_crm.event_admin_access_added", {defaultValue: "Přidělen administrátorský přístup"});
            if (beforeAdmin === true && afterAdmin !== true) return t("admin.project_crm.event_admin_access_removed", {defaultValue: "Odebrán administrátorský přístup"});
            if (operation === "delete") return t("admin.project_crm.event_admin_access_removed", {defaultValue: "Odebrán administrátorský přístup"});
            return t("admin.project_crm.event_admin_access_changed", {defaultValue: "Změna administrátorského přístupu"});
        }
        if (operation === "insert") return t("admin.project_crm.event_member_added", {defaultValue: "Člen projektu přidán"});
        if (operation === "delete") return t("admin.project_crm.event_member_removed", {defaultValue: "Člen projektu odebrán"});
        return t("admin.project_crm.event_membership_changed", {defaultValue: "Změna členství"});
    }
    if (["note", "crm_project_note"].includes(event?.entityType)) {
        if (operation === "insert") return t("admin.project_crm.event_note_added", {defaultValue: "Přidána poznámka"});
        if (operation === "delete") return t("admin.project_crm.event_note_removed", {defaultValue: "Odebrána poznámka"});
        return t("admin.project_crm.event_note_updated", {defaultValue: "Upravena poznámka"});
    }
    if (event?.entityType === "project") {
        if (operation === "insert") return t("admin.project_crm.event_project_created", {defaultValue: "Projekt vytvořen"});
        if (operation === "delete" || (operation === "update" && beforeDeleted !== true && afterDeleted === true)) return t("admin.project_crm.event_project_deleted", {defaultValue: "Projekt smazán"});
        if (operation === "update" && beforeDeleted === true && afterDeleted === false) return t("admin.project_crm.event_project_restored", {defaultValue: "Projekt obnoven"});
        if (operation === "update" && fields.includes("name")) return t("admin.project_crm.event_project_name_changed", {defaultValue: "Změna názvu projektu"});
        if (operation === "update" && fields.includes("primaryContactUserId")) return t("admin.project_crm.event_primary_contact_changed", {defaultValue: "Změna hlavního kontaktu"});
        if (operation === "update" && fields.includes("crmStatus")) return t("admin.project_crm.event_crm_status_changed", {defaultValue: "Změna CRM stavu"});
    }
    if (event?.entityType === "opportunity" && operation === "insert") return t("admin.project_crm.event_opportunity_created", {defaultValue:"Příležitost vytvořena"});
    if (fields.includes("crmStatus")) return t(operation === "insert" ? "admin.project_crm.event_crm_status_set" : "admin.project_crm.event_crm_status_changed", {defaultValue: operation === "insert" ? "Nastaven CRM stav" : "Změna CRM stavu"});
    if (fields.includes("primaryContactUserId")) return t(operation === "insert" ? "admin.project_crm.event_primary_contact_set" : "admin.project_crm.event_primary_contact_changed", {defaultValue: operation === "insert" ? "Nastaven hlavní kontakt" : "Změna hlavního kontaktu"});
    if (event?.entityType === "project" && operation === "update" && fields.includes("name")) return t("admin.project_crm.event_project_name_changed", {defaultValue: "Změna názvu projektu"});
    if (["billing_account", "billingAccount"].includes(event?.entityType) || fields.some((field) => field.startsWith("billing."))) {
        if (operation === "insert") return t("admin.project_crm.event_billing_added", {defaultValue: "Přidány fakturační údaje"});
        if (operation === "delete") return t("admin.project_crm.event_billing_removed", {defaultValue: "Odebrány fakturační údaje"});
        return t("admin.project_crm.event_billing_changed", {defaultValue: "Změna fakturačních údajů"});
    }
    if (["subscription", "project_subscription", "project_subscription_packages"].includes(event?.entityType) || fields.some((field) => field.startsWith("subscription."))) {
        if (operation === "insert") return t("admin.project_crm.event_subscription_added", {defaultValue: "Předplatné přidáno"});
        if (operation === "delete") return t("admin.project_crm.event_subscription_removed", {defaultValue: "Předplatné odebráno"});
        return t("admin.project_crm.event_subscription_changed", {defaultValue: "Změna předplatného"});
    }
    if (["project_contact", "user", "project_user_profile"].includes(event?.entityType)) {
        if (operation === "insert") return t("admin.project_crm.event_contact_added", {defaultValue: "Kontakt přidán"});
        if (operation === "delete") return t("admin.project_crm.event_contact_removed", {defaultValue: "Kontakt odebrán"});
        return t("admin.project_crm.event_contact_changed", {defaultValue: "Změna kontaktních údajů"});
    }
    const entity = eventEntityLabel(event?.entityType, t);
    if (entity !== t("admin.project_crm.event_entity_unknown", {defaultValue: "Změna"})) return `${entity} · ${eventOperationLabel(operation, t, event?.entityType)}`;
    if (operation === "insert") return t("admin.project_crm.event_record_insert", {defaultValue: "Vytvořen záznam"});
    if (operation === "delete") return t("admin.project_crm.event_record_delete", {defaultValue: "Odebrán záznam"});
    if (operation === "snapshot") return t("admin.project_crm.event_record_snapshot", {defaultValue: "Zachycen stav záznamu"});
    return t("admin.project_crm.event_record_update", {defaultValue: "Upraven záznam"});
}
